import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import PostCard from '../components/post/PostCard';
import { BookOpen, Grape, Utensils, Flower2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

// Carousel Images
import slide1 from '../assets/carousel/01.jpg';
import slide2 from '../assets/carousel/02.jpg';
import slide3 from '../assets/carousel/03.jpg';
import slide4 from '../assets/carousel/04.jpg';
import slide5 from '../assets/carousel/05.jpg';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [visibleCount, setVisibleCount] = useState(6);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselTimerRef = useRef(null);

  // Memoize slides to prevent recreation on every render
  const localSlides = useMemo(() => [
    {
      url: slide1,
      title: t('homepage.slides.s1_title'),
      sub: t('homepage.slides.s1_sub'),
      caption: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards',
      alt: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards'
    },
    {
      url: slide2,
      title: t('homepage.slides.s2_title'),
      sub: t('homepage.slides.s2_sub'),
      caption: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes',
      alt: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes'
    },
    {
      url: slide3,
      title: t('homepage.slides.s3_title'),
      sub: t('homepage.slides.s3_sub'),
      caption: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view',
      alt: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view'
    },
    {
      url: slide4,
      title: t('homepage.slides.s4_title'),
      sub: t('homepage.slides.s4_sub'),
      caption: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor',
      alt: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor'
    },
    {
      url: slide5,
      title: t('homepage.slides.s5_title'),
      sub: t('homepage.slides.s5_sub'),
      caption: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles',
      alt: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles'
    }
  ], [t, currentLang]); // Add dependencies

  // Memoize categories to prevent recreation
  const categories = useMemo(() => [
    { name: t('categories.learn-discover'), path: '/category/learn-discover', icon: BookOpen },
    { name: t('categories.region-grape'), path: '/category/region-grape', icon: Grape },
    { name: t('categories.foodpairing-lifestyle'), path: '/category/foodpairing-lifestyle', icon: Utensils },
    { name: t('categories.health-beauty'), path: '/category/health-beauty', icon: Flower2 }
  ], [t]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === localSlides.length - 1 ? 0 : prev + 1));
  }, [localSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? localSlides.length - 1 : prev - 1));
  }, [localSlides.length]);

  // Reset timer when slide changes manually
  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    // Reset timer
    if (carouselTimerRef.current) {
      clearInterval(carouselTimerRef.current);
      carouselTimerRef.current = setInterval(nextSlide, 5000);
    }
  }, [nextSlide]);

  // Improved carousel timer management
  useEffect(() => {
    carouselTimerRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
      }
    };
  }, [nextSlide]);

  // Fetch posts with error handling and abort controller
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data.json', {
          signal: abortController.signal,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!response.ok) throw new Error(`Failed to load posts: ${response.status}`);
        const allPosts = await response.json();
        
        // Validate that allPosts is an array
        if (!Array.isArray(allPosts)) {
          throw new Error('Invalid data format: expected array');
        }
        
        setPosts(allPosts);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error("Error loading static data:", err);
          setPosts([]);
          // You might want to show a user-friendly error message here
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();

    return () => {
      abortController.abort();
    };
  }, []); // Remove sortBy dependency - we'll sort in memory

  // Memoize sorted posts to avoid sorting on every render
  const sortedPosts = useMemo(() => {
    if (!posts.length) return [];
    
    return [...posts].sort((a, b) => {
      if (sortBy === 'views') {
        return (b.view_count || 0) - (a.view_count || 0);
      } else {
        const dateA = new Date(a.date || a.created_at || 0);
        const dateB = new Date(b.date || b.created_at || 0);
        return dateB - dateA;
      }
    });
  }, [posts, sortBy]);

  // Memoize visible posts
  const visiblePosts = useMemo(() => 
    sortedPosts.slice(0, visibleCount),
    [sortedPosts, visibleCount]
  );

  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => prev + 3);
  }, []);

  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setVisibleCount(6); // Reset visible count when sorting changes
  }, []);

  // Early return if slide doesn't exist
  if (!localSlides[currentSlide]) {
    return null;
  }

  return (
    <main className="flex flex-col bg-cream min-h-screen overflow-x-hidden">
      <SEO
        title={t('homepage.seo_title') !== 'homepage.seo_title' ? t('homepage.seo_title') : "Euro Wine Trip | European Wine Culture and Perspectives"}
        description={t('homepage.seo_description') !== 'homepage.seo_description' ? t('homepage.seo_description') : "Professional Wine Blog exploring European terroir and stories."}
        image="/logo-share.jpg"
        slug=""
        type="website"
        keywords={currentLang === 'zh'
          ? "葡萄酒, 欧洲酒庄, 品鉴, 普罗旺斯, 勃艮第, 摩泽尔"
          : "wine, european wineries, tasting, bordeaux, burgundy, mosel"
        }
        aiSummary={currentLang === 'zh'
          ? "Euro Wine Trip 致力于通过专业视角揭秘欧洲顶级酒庄，提供最真实的葡萄酒故事与文化。"
          : "Euro Wine Trip provides professional perspectives on Europe's finest vineyards, sharing authentic wine stories and culture."
        }
      />
      
 

      <div className="h-20 md:h-24 w-full" aria-hidden="true" />

      {/* HERO CAROUSEL SECTION */}
      <section className="relative w-screen h-[450px] md:h-[700px] bg-dark-olive shadow-2xl overflow-hidden mt-4 md:mt-8 left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] group">
        <div className="relative w-full h-full">
          <figure className="w-full h-full m-0 p-0 relative">
            <img
              src={localSlides[currentSlide].url}
              alt={localSlides[currentSlide].alt}
              className="w-full h-full object-cover opacity-80 transition-opacity duration-1000 ease-in-out"
              loading="eager"
              fetchPriority="high"
            />
            <figcaption className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 bg-black/40 backdrop-blur-md px-6 py-2 rounded-full text-[11px] md:text-xs text-white/80 font-serif italic tracking-wide opacity-0 group-hover:opacity-100 transition-all duration-500 border border-white/10 shadow-lg">
              {localSlides[currentSlide].caption}
            </figcaption>
          </figure>
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6 pointer-events-none">
            <p className="text-3xl md:text-6xl font-serif italic mb-4 drop-shadow-2xl">
              {localSlides[currentSlide].title}
            </p>
            <p className="text-[10px] md:text-lg uppercase tracking-[0.5em] mb-10 opacity-90 font-light">
              {localSlides[currentSlide].sub}
            </p>
            
            <div className="flex gap-4 pointer-events-auto" role="tablist">
              {localSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index ? 'bg-white scale-150' : 'bg-white/30 hover:bg-white/60'
                  }`}
                  aria-label={`${t('homepage.slide')} ${index + 1}`}
                  aria-current={currentSlide === index ? 'true' : 'false'}
                />
              ))}
            </div>
          </div>

          <button 
            onClick={prevSlide} 
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 z-40"
            aria-label={t('common.previous')}
          >
            <ChevronLeft size={60} strokeWidth={1} />
          </button>
          
          <button 
            onClick={nextSlide} 
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 z-40"
            aria-label={t('common.next')}
          >
            <ChevronRight size={60} strokeWidth={1} />
          </button>
        </div>
      </section>

      {/* CATEGORY NAVIGATION */}
      <nav aria-label={t('homepage.category_navigation')} className="sticky top-0 z-40 bg-cream/95 backdrop-blur-md py-8 border-b border-olive/10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={cat.path}
                  to={cat.path}
                  className="bg-olive h-28 md:h-36 shadow-lg flex flex-col items-center justify-center p-4 text-center group transition-all duration-500 rounded-[1.5rem] md:rounded-[2.5rem] text-white hover:bg-dark-olive hover:scale-[1.05] hover:shadow-2xl"
                >
                  <Icon
                    className="w-7 h-7 md:w-10 md:h-10 mb-3 transition-colors duration-300 group-hover:text-[#800020]"
                    strokeWidth={1.2}
                    aria-hidden="true"
                  />
                  <span className="font-bold uppercase tracking-widest text-[11px] md:text-[13px] transition-colors duration-300 group-hover:text-[#800020]">
                    {cat.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* LATEST POSTS SECTION */}
      <section className="container mx-auto px-4 md:px-6 mb-24 mt-20" aria-label={t('homepage.posts_section')}>
        <div className="flex flex-col md:flex-row justify-between items-center mb-16 border-b border-olive/20 pb-8 gap-8">
          <h2 className="text-2xl md:text-4xl font-serif text-olive italic tracking-tight">
            {t('homepage.latest_stories')}
          </h2>
          
          <div className="flex bg-olive/5 p-1.5 rounded-full border border-olive/20">
            <button
              onClick={() => handleSortChange('created_at')}
              className={`px-8 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 ${
                sortBy === 'created_at' ? 'bg-olive text-white shadow-lg' : 'text-olive hover:bg-olive/10'
              }`}
              aria-pressed={sortBy === 'created_at'}
            >
              {t('homepage.recent')}
            </button>
            <button
              onClick={() => handleSortChange('views')}
              className={`px-8 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 ${
                sortBy === 'views' ? 'bg-olive text-white shadow-lg' : 'text-olive hover:bg-olive/10'
              }`}
              aria-pressed={sortBy === 'views'}
            >
              {t('homepage.popular')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="h-96 flex flex-col justify-center items-center gap-4" role="status">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive"></div>
            <p className="font-serif italic text-olive">{t('homepage.loading')}</p>
            <span className="sr-only">{t('common.loading')}</span>
          </div>
        ) : (
          <>
            {visiblePosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-20">
                {visiblePosts.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-olive/60 font-serif italic">{t('homepage.no_posts')}</p>
              </div>
            )}

            {!loading && sortedPosts.length > visibleCount && (
              <div className="flex justify-center mt-20">
                <button
                  onClick={handleLoadMore}
                  className="border-[1px] border-olive text-olive px-12 py-4 rounded-full font-bold tracking-[0.2em] uppercase text-xs hover:bg-olive hover:text-white transition-all duration-500 shadow-sm"
                  aria-label={t('homepage.load_more_aria')}
                >
                  {t('homepage.load_more')}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}